import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { AdminColors } from '../../Admin/constants/adminColors';
import { WebImage } from '../../PerfectTruck/PTPhotoUpload';
import { useDarkStyles } from '../../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TruckPhotoGridProps {
  /**
   * Map of photo label → image URI (or null/empty when no photo was taken for
   * that category). Categories with a null/empty URI render as a placeholder.
   */
  photos: Record<string, string | null>;
  /**
   * Width of the containing card (excluding its horizontal padding) used to
   * compute per-thumbnail widths.  Pass the value from an `onLayout` callback.
   * When 0 or omitted the thumbnails use `flex: 1` sizing.
   */
  containerWidth?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MOBILE_BREAKPOINT = 768;
const DESKTOP_COLS = 3;
const MOBILE_COLS = 2;
const GAP = 10;

// ─── Component ────────────────────────────────────────────────────────────────

const TruckPhotoGrid: React.FC<TruckPhotoGridProps> = ({
  photos,
  containerWidth = 0,
}) => {
  const styles = useDarkStyles(baseStyles);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const cols = isMobile ? MOBILE_COLS : DESKTOP_COLS;
  const totalGaps = (cols - 1) * GAP;
  // Fixed pixel width when we know the container; percentage fallback otherwise.
  // Never use flex: 1 — it causes the last item in a row to stretch full-width.
  const thumbWidth =
    containerWidth > 0
      ? Math.floor((containerWidth - totalGaps) / cols)
      : null;
  // Percentage string that approximates 1/cols minus gap allowance
  const thumbPct = isMobile ? '48%' : '31%';

  // Lightbox state: index into photo-only entries
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Build a flat list of only the entries that have a real URI (for lightbox nav)
  const entries = Object.entries(photos);
  const photoEntries = entries.filter(([, uri]) => uri && uri.trim() !== '');

  const openLightbox = useCallback(
    (uri: string) => {
      const idx = photoEntries.findIndex(([, u]) => u === uri);
      setLightboxIndex(idx >= 0 ? idx : null);
    },
    [photoEntries],
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goPrev = useCallback(() => {
    setLightboxIndex(prev =>
      prev !== null && prev > 0 ? prev - 1 : prev,
    );
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex(prev =>
      prev !== null && prev < photoEntries.length - 1 ? prev + 1 : prev,
    );
  }, [photoEntries.length]);

  // Escape key support (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || lightboxIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  if (entries.length === 0) return null;

  const lightboxUri =
    lightboxIndex !== null ? photoEntries[lightboxIndex]?.[1] ?? null : null;
  const lightboxLabel =
    lightboxIndex !== null ? photoEntries[lightboxIndex]?.[0] ?? '' : '';

  const modalImageSize = Math.min(windowWidth * 0.88, windowHeight * 0.72);

  // ── Single thumb ──────────────────────────────────────────────────────────

  const renderThumb = (label: string, uri: string | null) => {
    const wrapperStyle: any[] = [
      styles.thumbWrapper,
      thumbWidth ? { width: thumbWidth } : { width: thumbPct },
    ];

    return (
      <View key={label} style={wrapperStyle}>
        {uri && uri.trim() !== '' ? (
          <TouchableOpacity activeOpacity={0.75} onPress={() => openLightbox(uri)}>
            <WebImage uri={uri} style={styles.thumb} resizeMode="cover" />
          </TouchableOpacity>
        ) : (
          <View style={[styles.thumb, styles.placeholder]}>
            <MaterialIcons name="photo-camera" size={28} color={AdminColors.textMuted} />
          </View>
        )}
        <Text style={styles.thumbLabel} numberOfLines={1} ellipsizeMode="tail">
          {label}
        </Text>
      </View>
    );
  };

  return (
    <>
      {/* ── Responsive photo grid ────────────────────────────────────────── */}
      <View style={styles.grid}>
        {entries.map(([label, uri]) => renderThumb(label, uri))}
      </View>

      {/* ── Lightbox — rendered in a true Modal portal, covers the full screen ── */}
      <Modal
        visible={lightboxIndex !== null}
        transparent
        animationType="fade"
        onRequestClose={closeLightbox}
        statusBarTranslucent
      >
        <Pressable style={styles.backdrop} onPress={closeLightbox}>
          {/* Content box: image, close button, caption */}
          <Pressable style={styles.lightboxContent} onPress={e => e.stopPropagation()}>
            {lightboxIndex !== null && (() => {
              const idx: number = lightboxIndex;
              return (
                <>
                  {/* ✕ Close button */}
                  <TouchableOpacity
                    style={styles.closeBtn}
                    onPress={closeLightbox}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <MaterialIcons name="close" size={24} color="#fff" />
                  </TouchableOpacity>

                  {/* Full-size image */}
                  {lightboxUri ? (
                    <WebImage
                      uri={lightboxUri}
                      style={[styles.lightboxImage, { width: modalImageSize, height: modalImageSize }]}
                      resizeMode="contain"
                    />
                  ) : null}

                  {/* Label + counter */}
                  <View style={styles.captionRow}>
                    <Text style={styles.captionLabel}>{lightboxLabel}</Text>
                    <Text style={styles.captionCounter}>
                      {idx + 1} / {photoEntries.length}
                    </Text>
                  </View>
                </>
              );
            })()}
          </Pressable>

          {/* Nav arrows — anchored to full-screen backdrop so they stay within screen bounds */}
          <TouchableOpacity
            style={[styles.navBtn, styles.navBtnLeft, lightboxIndex === 0 && styles.navBtnDisabled]}
            onPress={goPrev}
            disabled={lightboxIndex === 0}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <MaterialIcons name="chevron-left" size={36} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.navBtn, styles.navBtnRight, lightboxIndex === photoEntries.length - 1 && styles.navBtnDisabled]}
            onPress={goNext}
            disabled={lightboxIndex === photoEntries.length - 1}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <MaterialIcons name="chevron-right" size={36} color="#fff" />
          </TouchableOpacity>
        </Pressable>
      </Modal>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const baseStyles = StyleSheet.create({
  // ── Grid ────────────────────────────────────────────────────────────────────
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: GAP,
    marginBottom: 14,
    alignItems: 'flex-start',
  },
  thumbWrapper: {
    // width is always set inline (fixed px or % fallback) — never flex: 1
    // so the last item in a partial row never stretches to fill remaining space
    minWidth: 80,
  },
  thumb: {
    width: '100%',
    aspectRatio: 16 / 10,
    borderRadius: 8,
    backgroundColor: AdminColors.gray50,
  },
  placeholder: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderStyle: 'dashed',
  },
  thumbLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 10,
    color: AdminColors.textMuted,
    textAlign: 'center',
    marginTop: 4,
  },

  // ── Lightbox ────────────────────────────────────────────────────────────────
  // The Modal already fills the full screen; the backdrop just needs flex: 1
  // so it stretches to fill whatever the Modal gives it.
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lightboxContent: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lightboxImage: {
    borderRadius: 8,
  },
  closeBtn: {
    position: 'absolute',
    top: -44,
    right: -4,
    zIndex: 10,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 20,
    padding: 4,
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    zIndex: 10,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 24,
    padding: 4,
    // vertically centre the button
    marginTop: -24,
  },
  navBtnLeft: {
    left: 12,
  },
  navBtnRight: {
    right: 12,
  },
  navBtnDisabled: {
    opacity: 0.25,
  },
  captionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    width: '100%',
    paddingHorizontal: 2,
  },
  captionLabel: {
    color: '#fff',
    fontSize: 13,
    fontFamily: FontFamily.bold,
    flexShrink: 1,
  },
  captionCounter: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    marginLeft: 8,
  },
});

export default TruckPhotoGrid;
